<script lang="ts" setup>
import type { SchoolsLandingStepCard } from '~kui/components/KUI/Schools/LandingStepCardSection/KUISchoolsLandingStepCardSection.vue';

import { Button } from '~ds/components/ui/button';
import { PageContainer } from '~ds/components/ui/page-container';
import { TextHighlight } from '~ds/components/ui/text-highlight';
import { ICONS } from '~ds/lib/icons';

import { useSchoolsDemoLink } from '~/composables/Schools/useSchoolsDemoLink';
import { EDUCATORS_ROUTE_LOGIN, EDUCATORS_ROUTE_SIGNUP } from '~/constants/routes/educators';
import { FaqSection } from '~/graphql/generated/graphql';
import { useConfigStore } from '~/stores/config';

const { t } = useI18n();

// Acquisition CTAs land on signup while teacher registration is open.
const ctaRoute = useConfigStore().teacherRegistrationOpen ? EDUCATORS_ROUTE_SIGNUP : EDUCATORS_ROUTE_LOGIN;
const { isAus, isUsa } = useClusterCountryConfig();

useSeoMeta({
  title: t('schools.landing.seo.title'),
  description: t('schools.landing.seo.description'),
  ogTitle: t('schools.landing.seo.title'),
  ogDescription: t('schools.landing.seo.description'),
  ogType: 'website',
});

// The demo request page is gated on this flag and bounces to the login when it is off, so
// the CTA is dropped rather than offered as a link that lands somewhere else.
const { getFlag } = useFeatureFlags();
const schoolsEnquiryEnabled = await getFlag('schools-enquiry-enabled');

const demoLink = useSchoolsDemoLink();

const analytics = useAnalyticsService();
const { ensurePageView, trackLead } = useSchoolsMetaPixel();
onMounted(() => {
  analytics.track(EVENTS.SCHOOLS_LANDING_VIEWED);
  ensurePageView();
});

const trackCta = (cta: string) => analytics.track(EVENTS.SCHOOLS_LANDING_CTA_CLICKED, { cta });

const trackTryActivity = (cta: string) => {
  trackCta(cta);
  trackLead();
};

const activityCards: SchoolsLandingStepCard[] = [
  {
    key: 'find',
    icon: ICONS.assignments,
    title: t('schools.landing.activities.cards.find.title'),
    description: t('schools.landing.activities.cards.find.description'),
  },
  {
    key: 'personalise',
    icon: ICONS.id,
    title: t('schools.landing.activities.cards.personalise.title'),
    description: t('schools.landing.activities.cards.personalise.description'),
  },
  {
    key: 'report',
    icon: ICONS.audit,
    title: t('schools.landing.activities.cards.report.title'),
    description: t('schools.landing.activities.cards.report.description'),
  },
];

const howItWorksItems = ['choose', 'share', 'coach', 'feedback']
  .map(key => t(`schools.landing.howItWorks.items.${key}`));
</script>

<template>
  <SchoolsPublicShell>
    <PageContainer size="xl">
      <KUISchoolsLandingHero
        :title="t('schools.landing.hero.title')"
        :description="t('schools.landing.hero.description')"
        :tag-label="t('schools.landing.hero.earlyAccess')"
        :illustration="isUsa ? '/images/kui/schools/landing/us-impact-report-feature.webp' : '/images/kui/schools/landing/au-impact-report-feature.webp'"
        size="md"
      >
        <template #actions>
          <Button size="lg" as-child>
            <NuxtLink :to="ctaRoute" @click="trackTryActivity('hero_try_activity')">
              {{ t('schools.landing.hero.ctaPrimary') }}
            </NuxtLink>
          </Button>
          <Button
            v-if="schoolsEnquiryEnabled"
            variant="outline"
            size="lg"
            as-child
          >
            <NuxtLink v-bind="demoLink" @click="trackCta('hero_see_how')">
              {{ t('schools.landing.hero.ctaSecondary') }}
            </NuxtLink>
          </Button>
        </template>
      </KUISchoolsLandingHero>
    </PageContainer>

    <PageContainer size="xl">
      <KUISchoolsLandingSectionBlock
        :description="t('schools.landing.problem.description')"
        spotlight-position="bottom-left"
      >
        <template #title>
          <i18n-t keypath="schools.landing.problem.title" scope="global">
            <template #highlight>
              <TextHighlight color="lime">
                {{ t('schools.landing.problem.titleHighlight') }}
              </TextHighlight>
            </template>
          </i18n-t>
        </template>
      </KUISchoolsLandingSectionBlock>
    </PageContainer>

    <PageContainer size="xl">
      <KUISchoolsLandingStepCardSection
        :title="t('schools.landing.activities.title')"
        :label="t('schools.landing.activities.available')"
        :cards="activityCards"
      />
    </PageContainer>

    <PageContainer v-if="isUsa" size="xl">
      <KUISchoolsLandingSectionBlock
        surface="bare"
        :rounded="false"
        :label="t('schools.landing.howItWorks.label')"
        :items="howItWorksItems"
        illustration="/images/kui/schools/landing/impact-report-sample.webp"
      >
        <template #title>
          {{ t('schools.landing.howItWorks.title') }}
        </template>
      </KUISchoolsLandingSectionBlock>
    </PageContainer>

    <!-- The impact report runs full bleed, so it is the one section with no container. -->
    <KUISchoolsLandingSectionBlock
      :description="t('schools.landing.impactReport.description')"
      :rounded="false"
      :illustration="isAus ? '/images/kui/schools/landing/pink-todo-list.webp' : undefined"
    >
      <template #title>
        <i18n-t keypath="schools.landing.impactReport.title" scope="global">
          <template #highlight>
            <TextHighlight color="lime">
              {{ t('schools.landing.impactReport.titleHighlight') }}
            </TextHighlight>
          </template>
        </i18n-t>
      </template>
    </KUISchoolsLandingSectionBlock>

    <PageContainer size="xl">
      <KUISchoolsLandingSectionBlock
        surface="bare"
        :rounded="false"
        :description="t('schools.landing.teacherControl.description')"
        illustration="/images/illustrations/blinky-chat.webp"
      >
        <template #title>
          <i18n-t keypath="schools.landing.teacherControl.title" scope="global">
            <template #highlight>
              <TextHighlight color="lime">
                {{ t('schools.landing.teacherControl.titleHighlight') }}
              </TextHighlight>
            </template>
          </i18n-t>
        </template>
      </KUISchoolsLandingSectionBlock>
    </PageContainer>

    <SchoolsLandingFaq
      :section="FaqSection.SchoolsLanding"
      :title="t('schools.landing.faqs.title')"
    />

    <PageContainer size="xl">
      <KUISchoolsLandingPayoff
        :title="t('schools.landing.payoff.title')"
        :description="t('schools.landing.payoff.description')"
      >
        <template #actions>
          <Button
            variant="secondary"
            size="2xl"
            as-child
          >
            <NuxtLink :to="ctaRoute" @click="trackTryActivity('payoff_try_activity')">
              {{ t('schools.landing.payoff.cta') }}
            </NuxtLink>
          </Button>
        </template>
      </KUISchoolsLandingPayoff>
    </PageContainer>
  </SchoolsPublicShell>
</template>
